(() => {
  'use strict';

  const feedSelector = '[data-cw-soil-feed]';
  const railSelector = '[data-cw-soil-rail]';
  const instances = new Map();
  const maximumRequests = 100;
  const requestTimeout = 10000;

  function cardData(card) {
    const id = card.dataset.articleId || '';
    const published = card.dataset.publishedAt || '';
    if (card.tagName !== 'LI' || !/^[1-9]\d*$/.test(id) || !/^\d+$/.test(published)) {
      throw new Error('Invalid tutorial card');
    }
    const timestamp = Number(published);
    if (!Number.isSafeInteger(timestamp)) throw new Error('Invalid publication date');
    return { card, id, timestamp };
  }

  function orderedCards(rail, incoming = []) {
    const cards = new Map();
    for (const card of [...rail.children, ...incoming]) {
      const item = cardData(card);
      if (!cards.has(item.id)) cards.set(item.id, item);
    }
    return [...cards.values()].sort((a, b) => {
      if (a.timestamp !== b.timestamp) return b.timestamp - a.timestamp;
      if (a.id.length !== b.id.length) return b.id.length - a.id.length;
      return a.id === b.id ? 0 : a.id > b.id ? -1 : 1;
    }).map(item => item.card);
  }

  function pageUrl(rawUrl, sectionId) {
    const url = new URL(rawUrl, window.location.href);
    const pathname = window.location.pathname.replace(/\/$/, '') || '/';
    const candidatePath = url.pathname.replace(/\/$/, '') || '/';
    const page = url.searchParams.get('page') || '';
    if (!/^https?:$/.test(url.protocol) || url.origin !== window.location.origin ||
        url.username || url.password || candidatePath !== pathname ||
        !/^[1-9]\d*$/.test(page) || Number(page) < 2 || Number(page) > 25000) {
      throw new Error('Invalid pagination URL');
    }
    url.hash = '';
    url.searchParams.set('section_id', sectionId);
    url.searchParams.sort();
    return url;
  }

  function initializeFeed(root) {
    if (root.dataset.cwSoilInitialized === 'true') return;
    const rail = root.querySelector(railSelector);
    if (!rail) return;
    root.dataset.cwSoilInitialized = 'true';
    const previous = root.querySelector('[data-cw-soil-prev]');
    const next = root.querySelector('[data-cw-soil-next]');
    const controls = root.querySelector('[data-cw-soil-controls]');
    const count = root.querySelector('[data-cw-soil-count]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let observer;
    let pendingFrame = 0;
    let requestController;
    let stopped = false;

    function updateControls() {
      pendingFrame = 0;
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const scrollLeft = rail.scrollLeft;
      const cardCount = String(rail.children.length);
      const controlsHidden = maximum <= 1;
      const previousDisabled = scrollLeft <= 1;
      const nextDisabled = scrollLeft >= maximum - 1;
      if (controls && controls.hidden !== controlsHidden) controls.hidden = controlsHidden;
      if (previous && previous.disabled !== previousDisabled) previous.disabled = previousDisabled;
      if (next && next.disabled !== nextDisabled) next.disabled = nextDisabled;
      if (count && count.textContent !== cardCount) count.textContent = cardCount;
    }

    function scheduleControls() {
      if (!pendingFrame && !stopped) pendingFrame = window.requestAnimationFrame(updateControls);
    }

    function move(direction) {
      rail.scrollBy({
        left: direction * rail.clientWidth,
        behavior: reducedMotion.matches ? 'instant' : 'smooth'
      });
      scheduleControls();
    }

    function movePrevious() { move(-1); }
    function moveNext() { move(1); }
    function onKeydown(event) {
      if (event.target !== rail || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    }

    function applyCards(cards) {
      const oldCards = [...rail.children];
      const retained = new Set(cards);
      for (const oldCard of oldCards) {
        if (!retained.has(oldCard)) oldCard.remove();
      }
      cards.forEach((card, index) => {
        if (rail.children[index] !== card) rail.insertBefore(card, rail.children[index] || null);
      });
      scheduleControls();
    }

    async function loadRemaining() {
      let nextUrl = root.dataset.nextUrl || '';
      const sectionId = root.dataset.sectionId || '';
      const visited = new Set();
      let requests = 0;
      try {
        applyCards(orderedCards(rail));
        root.dataset.feedState = nextUrl ? 'loading' : 'complete';
        if (nextUrl && !sectionId) throw new Error('Missing section ID');
        while (nextUrl && !stopped && root.isConnected) {
          if (requests >= maximumRequests) throw new Error('Pagination limit reached');
          const url = pageUrl(nextUrl, sectionId);
          if (visited.has(url.href)) throw new Error('Pagination cycle');
          visited.add(url.href);
          requests += 1;
          requestController = new AbortController();
          const timer = window.setTimeout(() => requestController?.abort(), requestTimeout);
          let markup;
          try {
            const response = await fetch(url.href, {
              credentials: 'same-origin',
              headers: { Accept: 'text/html' },
              signal: requestController.signal
            });
            if (!response.ok) throw new Error('Section request failed');
            if (response.url && new URL(response.url).origin !== window.location.origin) {
              throw new Error('Unexpected section origin');
            }
            markup = await response.text();
          } finally {
            window.clearTimeout(timer);
            requestController = undefined;
          }
          if (stopped || !root.isConnected) return;
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          const feeds = [...parsed.querySelectorAll(feedSelector)]
            .filter(feed => feed.dataset.sectionId === sectionId &&
              feed.dataset.cwSoilKind === root.dataset.cwSoilKind);
          if (feeds.length !== 1) throw new Error('Missing section feed');
          const returnedRail = feeds[0].querySelector(railSelector);
          if (!returnedRail) throw new Error('Missing section rail');
          const cards = orderedCards(rail, [...returnedRail.children]);
          const followingUrl = feeds[0].dataset.nextUrl || '';
          if (followingUrl) pageUrl(followingUrl, sectionId);
          applyCards(cards);
          nextUrl = followingUrl;
          root.dataset.nextUrl = nextUrl;
        }
        if (!stopped && root.isConnected) root.dataset.feedState = 'complete';
      } catch {
        if (!stopped && root.isConnected) root.dataset.feedState = 'incomplete';
      }
    }

    previous?.addEventListener('click', movePrevious);
    next?.addEventListener('click', moveNext);
    rail.addEventListener('scroll', scheduleControls, { passive: true });
    rail.addEventListener('keydown', onKeydown);
    window.addEventListener('resize', scheduleControls);
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(scheduleControls);
      observer.observe(rail);
    }
    scheduleControls();
    instances.set(root, () => {
      stopped = true;
      requestController?.abort();
      observer?.disconnect();
      window.cancelAnimationFrame(pendingFrame);
      previous?.removeEventListener('click', movePrevious);
      next?.removeEventListener('click', moveNext);
      rail.removeEventListener('scroll', scheduleControls);
      rail.removeEventListener('keydown', onKeydown);
      window.removeEventListener('resize', scheduleControls);
      delete root.dataset.cwSoilInitialized;
      instances.delete(root);
    });
    void loadRemaining();
  }

  function initialize(scope = document) {
    if (scope.matches?.(feedSelector)) initializeFeed(scope);
    scope.querySelectorAll(feedSelector).forEach(initializeFeed);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, destroy] of instances) {
      if (event.target === root || event.target.contains(root)) destroy();
    }
  });
})();
